import { Button, color, Icon } from '@primer-io/goat'
import PrimerDocsLogo from 'assets/svg/primerdocs.svg'
import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import Clickable from 'components/Clickable'
import { AnimatePresence, motion } from 'framer-motion'
import useWindowSize from 'hooks/useWindowSize'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import breakpoints from '../../theme/breakpoints'
import { defaultSpring } from '../../utils/transitions'
import MobileMenu from '../SideMenu/MobileMenu'
import Spacer from '../Spacer'
import { ArrowUpRight, Close, Menu } from '@primer-io/goat-icons'

const HeaderContainer = styled.header`
	width: 100%;
	z-index: 2001;
	position: fixed;
	top: 0;
	left: 0;
`

const InnerHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	background-color: var(--color-white);
	height: 64px;
	padding: 0px 24px;
	border-bottom: 1px solid ${color('gray-200')};
`

const LogoWrapper = styled(Clickable)`
	display: block;
	width: 123px;
	height: 32px;

	svg {
		width: 100%;
		height: 100%;
	}
`

const ActionsWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
`

const DashboardButtonWrapper = styled.div`
	display: none;

	& > :not(:last-child) {
		margin-right: 20px;
	}
	${p => p.theme.above('large')`
		display: flex;
    align-items: center;
		height: 40px;
		width: fit-content;
	`}
`

const MobileMenuIcon = styled(Button)`
	margin-left: 24px;
	display: grid;
	place-items: center;

	${p => p.theme.above('large')`
		display: none;
	`}
`

const MobileMenuWrapper = styled(motion.div)`
	display: block;
	position: fixed;
	left: 0px;
	right: 0px;
	bottom: 0px;
	left: 0px;
	top: 64px;
	z-index: 2000;
	width: 100%;
	height: calc(100vh - 64px);
	overflow-y: auto;
	background: white;
`

export default function Header({ location }) {
	const [mobileMenuOpened, setMobileMenuOpened] = useState(false)
	const mobileMenuRef = useRef()
	const { width } = useWindowSize()

	useEffect(() => {
		if (mobileMenuOpened) disableBodyScroll(mobileMenuRef.current)
		else enableBodyScroll(mobileMenuRef.current)
	}, [mobileMenuOpened])

	useEffect(() => {
		if (width > breakpoints.large.width) setMobileMenuOpened(false)
	}, [width])

	return (
		<>
			<Spacer.V small='64px' />
			<HeaderContainer>
				<InnerHeader>
					<LogoWrapper
						to='/'
						aria-label='Primer Docs home'
						onClick={() => {
							setMobileMenuOpened(false)
						}}
					>
						<PrimerDocsLogo />
					</LogoWrapper>

					<ActionsWrapper>
						<DashboardButtonWrapper>
							<Button variant='subtle' as='a' href='https://primer.io/docs/api' target='_blank'>
								API Guide
								<Button.Icon as={ArrowUpRight} />
							</Button>

							<Button as='a' href='https://sandbox-dashboard.primer.io' target='_blank'>
								Dashboard
							</Button>
						</DashboardButtonWrapper>
						<MobileMenuIcon
							mobileMenuOpened={mobileMenuOpened}
							variant='ghost'
							onClick={() => {
								setMobileMenuOpened(!mobileMenuOpened)
							}}
						>
							<Icon as={mobileMenuOpened ? Close : Menu} />
						</MobileMenuIcon>
					</ActionsWrapper>
				</InnerHeader>
			</HeaderContainer>

			<AnimatePresence>
				{mobileMenuOpened && (
					<MobileMenuWrapper
						ref={mobileMenuRef}
						initial={{ opacity: 0.8, height: 0, y: -100, overflowY: 'hidden' }}
						animate={{ opacity: 1, height: 'auto', y: 0, overflowY: 'auto' }}
						exit={{ opacity: 0.8, height: 0, y: -100, overflowY: 'hidden' }}
						transition={defaultSpring}
					>
						<MobileMenu
							location={location}
							onClose={() => {
								clearAllBodyScrollLocks()
								setMobileMenuOpened(false)
							}}
						/>
					</MobileMenuWrapper>
				)}
			</AnimatePresence>
		</>
	)
}
